import { Minus, Wallet } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  StatTile,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useCashDrawer,
  useCashDrawerActions,
  useCheckers,
} from '@/features/billing/api';
import { formatRM, parseRM } from '@/features/billing/money';
import {
  METHOD_LABEL,
  type CashShift,
  type DrawerMovement,
  type PaymentMethod,
} from '@/features/billing/types';
import { useBranch } from '@/features/branch/branch-context';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import dash from '@/pages/DashboardPage.module.css';
import styles from '@/pages/queue/queue.module.css';

const NOTES = [
  { sen: 10000, label: 'RM 100 notes' },
  { sen: 5000, label: 'RM 50 notes' },
  { sen: 2000, label: 'RM 20 notes' },
  { sen: 1000, label: 'RM 10 notes' },
  { sen: 500, label: 'RM 5 notes' },
  { sen: 100, label: 'RM 1 notes' },
];

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

const MOVE_LABEL: Record<DrawerMovement['type'], string> = {
  payment: 'Payment',
  deposit: 'Deposit',
  refund: 'Refund',
  paid_out: 'Paid out',
};

/**
 * Admin v2 "Cash drawer" (artifacts #36–38, BIL-05): the counter's shift with
 * float, cash taken, paid out and what should be in the drawer; takings by
 * method, drawer movements and closed shifts with any difference.
 */
export function CashDrawerPage() {
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const { data, isLoading } = useCashDrawer();
  const act = useCashDrawerActions();
  const toast = useToast();
  const [float, setFloat] = useState('200.00');
  const [paying, setPaying] = useState(false);
  const [closing, setClosing] = useState(false);
  const can = hasPermission('cash_drawer:manage');
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: isApiError(err) ? (err.problem?.title ?? 'Not done') : 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });

  if (!branch)
    return (
      <PageContainer>
        <p className={styles.warnNote}>
          Choose the branch you are working at in the branch switch.
        </p>
      </PageContainer>
    );
  const s = data?.current ?? null;
  const floatSen = parseRM(float);

  return (
    <PageContainer>
      <div className={dash.tiles}>
        <StatTile
          highlight
          label="Expected in the drawer"
          value={s?.expectedSen != null ? formatRM(s.expectedSen) : '—'}
          caption={
            s
              ? `Float ${formatRM(s.floatSen)} + cash ${formatRM(s.cashTakenSen)} − paid out ${formatRM(s.paidOutSen)} − refunds ${formatRM(s.cashRefundsSen)}`
              : 'No open shift'
          }
        />
        <StatTile
          label="Cash taken"
          value={s ? formatRM(s.cashTakenSen) : '—'}
        />
        <StatTile label="Paid out" value={s ? formatRM(s.paidOutSen) : '—'} />
        <StatTile label="Float" value={s ? formatRM(s.floatSen) : '—'} />
      </div>

      <Card
        title={`Counter · ${branch.name}`}
        actions={
          s ? (
            <Badge tone="success">Shift open</Badge>
          ) : (
            <Badge>No shift open</Badge>
          )
        }
      >
        {isLoading ? null : s ? (
          <div className={styles.drawer}>
            <dl className={styles.rows}>
              <dt>Shift</dt>
              <dd className="mono">{s.number}</dd>
              <dt>Opened</dt>
              <dd>
                {time(s.openedAt)} by {s.openedBy.fullName} with a{' '}
                {formatRM(s.floatSen)} float
              </dd>
            </dl>
            {can && (
              <div className={styles.consultFooter}>
                <Button
                  icon={<Minus size={16} />}
                  onClick={() => setPaying(true)}
                >
                  Paid out
                </Button>
                <Button variant="primary" onClick={() => setClosing(true)}>
                  Count and close shift
                </Button>
              </div>
            )}
            <p className={styles.warnNote}>
              Every refund, void and discount needs a manager&apos;s approval
              before it touches the drawer. Paid-outs above{' '}
              {formatRM(data?.paidOutLimitSen ?? 5000)} need approval too.
              {s.pendingPaidOuts > 0 &&
                ` ${s.pendingPaidOuts} paid-out(s) waiting.`}
            </p>
          </div>
        ) : can ? (
          <form
            className={styles.dxForm}
            aria-label="Open the drawer"
            onSubmit={(ev) => {
              ev.preventDefault();
              if (floatSen !== null)
                void act.open.mutateAsync(floatSen).catch(fail);
            }}
          >
            <Input
              aria-label="Opening float (RM)"
              inputMode="decimal"
              value={float}
              onChange={(ev) => setFloat(ev.target.value)}
            />
            <Button
              type="submit"
              variant="primary"
              icon={<Wallet size={16} />}
              disabled={floatSen === null}
              loading={act.open.isPending}
            >
              Open shift
            </Button>
          </form>
        ) : (
          <p className={styles.sub}>No shift is open at this counter.</p>
        )}
      </Card>

      {s && <Takings shift={s} />}
      {s && <Movements shift={s} />}
      <ClosedShifts shifts={data?.closed ?? []} />

      {paying && (
        <PaidOutDialog
          limitSen={data?.paidOutLimitSen ?? 5000}
          onClose={() => setPaying(false)}
          onError={fail}
        />
      )}
      {closing && s && (
        <CloseShiftDialog
          shift={s}
          onClose={() => setClosing(false)}
          onError={fail}
        />
      )}
    </PageContainer>
  );
}

function Takings({ shift: s }: { shift: CashShift }) {
  const rows = Object.entries(s.takingsByMethod) as [PaymentMethod, number][];
  return (
    <Card title="Takings by method">
      {rows.length === 0 ? (
        <p className={styles.sub}>Nothing taken yet this shift.</p>
      ) : (
        <dl className={styles.rows}>
          {rows.map(([m, sen]) => (
            <div key={m} style={{ display: 'contents' }}>
              <dt>{METHOD_LABEL[m]}</dt>
              <dd>{formatRM(sen)}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}

function Movements({ shift: s }: { shift: CashShift }) {
  const columns: Column<DrawerMovement>[] = [
    { key: 'at', header: 'Time', render: (m) => time(m.at) },
    {
      key: 'type',
      header: 'Type',
      render: (m) => (
        <Badge
          tone={
            m.type === 'paid_out' || m.type === 'refund' ? 'warning' : 'neutral'
          }
        >
          {MOVE_LABEL[m.type]}
          {m.status === 'pending' ? ' · waiting' : ''}
          {m.status === 'rejected' ? ' · rejected' : ''}
        </Badge>
      ),
    },
    { key: 'method', header: 'Method', render: (m) => METHOD_LABEL[m.method] },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (m) => formatRM(m.amountSen),
    },
    { key: 'what', header: 'What', render: (m) => m.what },
    { key: 'by', header: 'By', render: (m) => m.by?.fullName ?? '—' },
    // RL-1822 (gap #111): refunds and large paid-outs show who approved them.
    {
      key: 'approvedBy',
      header: 'Approved by',
      render: (m) => m.approvedBy?.fullName ?? '—',
    },
  ];
  return (
    <Card title="Drawer movements this shift" flush>
      <DataTable
        caption="Drawer movements"
        columns={columns}
        rows={s.movements}
        rowKey={(m) => `${m.at}:${m.type}:${m.what}:${m.amountSen}`}
        empty={<EmptyState title="No movements yet" />}
      />
    </Card>
  );
}

function ClosedShifts({ shifts }: { shifts: CashShift[] }) {
  const columns: Column<CashShift>[] = [
    {
      key: 'shift',
      header: 'Shift',
      render: (s) => (
        <>
          <p className="mono">{s.number}</p>
          <p className={styles.sub}>
            {new Date(s.closedAt ?? s.openedAt).toLocaleString('en-GB')}
          </p>
        </>
      ),
    },
    {
      key: 'expected',
      header: 'Expected',
      align: 'right',
      render: (s) => (s.expectedSen != null ? formatRM(s.expectedSen) : '—'),
    },
    {
      key: 'counted',
      header: 'Counted',
      align: 'right',
      render: (s) => (s.countedSen != null ? formatRM(s.countedSen) : '—'),
    },
    {
      key: 'diff',
      header: 'Difference',
      render: (s) =>
        s.differenceSen ? (
          <>
            <Badge tone="warning">{formatRM(s.differenceSen)}</Badge>
            <p className={styles.sub}>{s.differenceReason}</p>
          </>
        ) : (
          <Badge tone="success">Balanced</Badge>
        ),
    },
    {
      key: 'people',
      header: 'People',
      render: (s) =>
        `${s.closedBy?.fullName ?? '—'} · checked by ${s.checkedBy?.fullName ?? '—'}`,
    },
  ];
  return (
    <Card title="Closed shifts" flush>
      <DataTable
        caption="Closed shifts"
        columns={columns}
        rows={shifts}
        rowKey={(s) => s.id}
        empty={<EmptyState title="No closed shifts yet" />}
      />
    </Card>
  );
}

/** "Paid out of the drawer" (artifact #37). */
function PaidOutDialog({
  limitSen,
  onClose,
  onError,
}: {
  limitSen: number;
  onClose: () => void;
  onError: (err: unknown) => void;
}) {
  const act = useCashDrawerActions();
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [receipt, setReceipt] = useState('');
  const sen = parseRM(amount);
  return (
    <Dialog
      open
      title="Paid out of the drawer"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!sen || reason.trim().length < 2}
            loading={act.paidOut.isPending}
            onClick={() =>
              sen &&
              act.paidOut
                .mutateAsync({
                  amountSen: sen,
                  reason: reason.trim(),
                  receiptNumber: receipt.trim() || undefined,
                })
                .then(() => {
                  toast.show({
                    tone: 'success',
                    title:
                      sen > limitSen
                        ? 'Waiting for a manager to approve'
                        : 'Paid out recorded',
                  });
                  onClose();
                })
                .catch(onError)
            }
          >
            Record
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <Field label="Amount (RM)">
          <Input
            inputMode="decimal"
            value={amount}
            onChange={(ev) => setAmount(ev.target.value)}
          />
        </Field>
        <Field label="What for">
          <Input
            placeholder="Courier for lab samples"
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
          />
        </Field>
        <Field label="Receipt number">
          <Input
            placeholder="Keep the receipt in the drawer"
            value={receipt}
            onChange={(ev) => setReceipt(ev.target.value)}
          />
        </Field>
        <p className={styles.sub}>
          Up to {formatRM(limitSen)} is recorded straight away. Above that, a
          manager approves first.
        </p>
      </div>
    </Dialog>
  );
}

/** "Close shift" (artifact #38): count first; the expected amount comes after. */
function CloseShiftDialog({
  shift: s,
  onClose,
  onError,
}: {
  shift: CashShift;
  onClose: () => void;
  onError: (err: unknown) => void;
}) {
  const act = useCashDrawerActions();
  const toast = useToast();
  const { data: checkers } = useCheckers(true);
  const [counts, setCounts] = useState<Record<number, string>>({});
  const [coins, setCoins] = useState('0.00');
  const [counted, setCounted] = useState(false);
  const [checker, setChecker] = useState('');
  const [why, setWhy] = useState('');
  const coinsSen = parseRM(coins) ?? 0;
  const total =
    NOTES.reduce((a, n) => a + n.sen * (Number(counts[n.sen]) || 0), 0) +
    coinsSen;
  const diff = s.expectedSen != null ? total - s.expectedSen : 0;
  return (
    <Dialog
      open
      title={`Close shift ${s.number}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {!counted ? (
            <Button variant="primary" onClick={() => setCounted(true)}>
              Finish count
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!checker || (diff !== 0 && why.trim().length < 3)}
              loading={act.close.isPending}
              onClick={() =>
                act.close
                  .mutateAsync({
                    notes: Object.fromEntries(
                      NOTES.map((n) => [
                        String(n.sen),
                        Number(counts[n.sen]) || 0,
                      ]),
                    ),
                    coinsSen,
                    checkedById: checker,
                    differenceReason: why.trim() || undefined,
                  })
                  .then(() => {
                    toast.show({ tone: 'success', title: 'Shift closed' });
                    onClose();
                  })
                  .catch(onError)
              }
            >
              Close shift
            </Button>
          )}
        </>
      }
    >
      <div className={styles.drawer}>
        <dl className={styles.rows}>
          <dt>Float</dt>
          <dd>{formatRM(s.floatSen)}</dd>
          <dt>Cash taken</dt>
          <dd>{formatRM(s.cashTakenSen)}</dd>
          <dt>Paid out</dt>
          <dd>-{formatRM(s.paidOutSen)}</dd>
          <dt>Cash refunds</dt>
          <dd>-{formatRM(s.cashRefundsSen)}</dd>
        </dl>
        <p className={styles.warnNote}>
          Count the notes and coins first. The expected amount shows only after
          you finish, so the count is not led by it.
        </p>
        <div className={styles.denoms}>
          {NOTES.map((n) => (
            <Field key={n.sen} label={n.label}>
              <Input
                inputMode="numeric"
                disabled={counted}
                value={counts[n.sen] ?? ''}
                placeholder="0"
                onChange={(ev) =>
                  setCounts({
                    ...counts,
                    [n.sen]: ev.target.value.replace(/\D/g, ''),
                  })
                }
              />
            </Field>
          ))}
          <Field label="Coins (RM)">
            <Input
              inputMode="decimal"
              disabled={counted}
              value={coins}
              onChange={(ev) => setCoins(ev.target.value)}
            />
          </Field>
        </div>
        <p>
          Counted <strong>{formatRM(total)}</strong>
        </p>
        {counted && (
          <>
            <p>
              Expected{' '}
              <strong>
                {s.expectedSen != null ? formatRM(s.expectedSen) : '—'}
              </strong>
              {' · '}
              {diff === 0 ? (
                <Badge tone="success">Balanced</Badge>
              ) : (
                <Badge tone="warning">{`Difference ${formatRM(diff)}`}</Badge>
              )}
            </p>
            <Field label="Checked by (second person)">
              <Select
                value={checker}
                onChange={(ev) => setChecker(ev.target.value)}
                options={[
                  { value: '', label: 'Choose…' },
                  ...(checkers ?? []).map((c) => ({
                    value: c.id,
                    label: c.fullName,
                  })),
                ]}
              />
            </Field>
            <Field
              label="If it does not balance, why"
              hint="Required when there is a difference"
            >
              <Input value={why} onChange={(ev) => setWhy(ev.target.value)} />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}
